import { useEffect, useState } from "react";
import { Button } from "@component/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  CardFooter,
} from "@component/card";
import { RadioGroup, RadioGroupItem } from "@component/radio-group";
import { Label } from "@component/label";
import { XIcon, BriefcaseIcon } from "lucide-react";
import {
  getWorkPreferences,
  createWorkPreferences,
  updateWorkPreferences,
} from "@service/cv";
import { CVWorkPreferences } from "@type/cv";
import NotificationMessage from "@component/notification-message";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
}

export function WorkPreferencesModal(props: Props) {
  const [workType, setWorkType] = useState("full-time");
  const [currency, setCurrency] = useState("USD");
  const [minSalary, setMinSalary] = useState(0);
  const [maxSalary, setMaxSalary] = useState(0);
  const [location, setLocation] = useState("remote");
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isLoadingUpdate, setLoadingUpdate] = useState<boolean>(false)
  const [isSaved, setSaved] = useState<boolean>(false)
  const [error, setError] = useState<string>("");
  const [notificationType, setNotificationType] = useState<string>("")
  const [notificationMessage, setNotificationMessage] = useState<string>("")
  const [workPreference, setWorkPreference] = useState<
    CVWorkPreferences | undefined
  >(undefined);

  const fetchWorkPreferences = async () => {
    setError("");

    try {
      const response = await getWorkPreferences();
      if (response.success && response.data) {
        if (
          response.data.preferred_job_type.length === 0 &&
          response.data.preferred_location.length === 0 &&
          response.data.salary_currency === "" &&
          response.data.salary_range_max === 0 &&
          response.data.salary_range_min === 0
        ) {
          setWorkPreference(undefined);
        } else {
          setWorkPreference(response.data);
        }

        setWorkType(response.data.preferred_job_type[0] || "full-time");
        setCurrency(response.data.salary_currency || "USD");
        setMinSalary(response.data.salary_range_min || 0);
        setMaxSalary(response.data.salary_range_max || 0);
      } else {
        setError(response.message || "Failed to fetch work preferences.");
      }
    } catch (error) {
      console.error("Error fetching work preferences:", error);
      setError("An unexpected error occurred.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (props.isOpen) {
      setSaved(false);
      fetchWorkPreferences();
    }
  }, [props.isOpen]);

  const handleSavePreference = async () => {
    setLoadingUpdate(true);
    setError("");
    setNotificationType("");
    setNotificationMessage("");

    const preferenceData: CVWorkPreferences = {
      preferred_job_type: [workType],
      preferred_location: [location],
      salary_currency: currency,
      salary_range_min: minSalary,
      salary_range_max: maxSalary,
    };

    try {
      const response = !workPreference
        ? await createWorkPreferences(preferenceData)
        : await updateWorkPreferences(preferenceData);

      if (response.success && response.data) {
        setWorkPreference(response.data);
        props.onSave();
        setSaved(true);
        setNotificationType("success");
        setNotificationMessage("Preferences saved.");
      } else {
        setError(response.message || "Failed to save work preferences.");
        setNotificationType("error");
        setNotificationMessage(response.message || "Failed to save work preferences.");
      }
    } catch (error) {
      console.error("Error saving work preferences:", error);
      setError("An unexpected error occurred while saving preferences.");
      setNotificationType("error");
      setNotificationMessage("Something went wrong. Please try again.");
    } finally {
      setLoadingUpdate(false);
    }
  };

  if (!props.isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      {isSaved && (
        <NotificationMessage
          type={notificationType}
          message={notificationMessage}
          duration={15000}
        />
      )}
      <div className="relative w-full max-w-md">
        <Card className="w-full shadow-lg">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <BriefcaseIcon className="h-6 w-6 text-blue-600" />
                <CardTitle className="text-xl font-bold">
                  Work Preferences
                </CardTitle>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={props.onClose}
                className="h-8 w-8"
              >
                <XIcon className="h-4 w-4" />
                <span className="sr-only">Close</span>
              </Button>
            </div>
            <CardDescription>
              Set your work preferences to find better job matches.
            </CardDescription>
          </CardHeader>
          <CardContent className="p-6 pt-4">
            {error && (
              <div className="mb-4 p-2 bg-red-50 text-red-600 rounded-md">
                {error}
              </div>
            )}
            <div className="flex flex-col gap-6">
              <div className="space-y-4">
                <div>
                  <Label
                    htmlFor="work-type"
                    className="text-sm font-medium block mb-2"
                  >
                    Work Type
                  </Label>
                  <RadioGroup
                    value={workType}
                    onValueChange={setWorkType}
                    className="flex flex-col space-y-2"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="full-time" id="full-time" />
                      <Label htmlFor="full-time" className="cursor-pointer">
                        Full-time
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="part-time" id="part-time" />
                      <Label htmlFor="part-time" className="cursor-pointer">
                        Part-time
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="contract" id="contract" />
                      <Label htmlFor="contract" className="cursor-pointer">
                        Contract/Project-based
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="hourly" id="hourly" />
                      <Label htmlFor="hourly" className="cursor-pointer">
                        Hourly
                      </Label>
                    </div>
                  </RadioGroup>
                </div>

                <div>
                  <Label
                    htmlFor="location-preference"
                    className="text-sm font-medium block mb-2"
                  >
                    Location Preference
                  </Label>
                  {!isLoading && (
                    <RadioGroup
                      value={location}
                      onValueChange={setLocation}
                      className="flex flex-col space-y-2"
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="remote" id="remote" />
                        <Label htmlFor="remote" className="cursor-pointer">
                          Remote
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="hybrid" id="hybrid" />
                        <Label htmlFor="hybrid" className="cursor-pointer">
                          Hybrid
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="onsite" id="onsite" />
                        <Label htmlFor="onsite" className="cursor-pointer">
                          Onsite
                        </Label>
                      </div>
                    </RadioGroup>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
          <CardFooter className="flex justify-end gap-2 pt-0">
            <Button variant="outline" onClick={props.onClose}>
              Cancel
            </Button>
            <Button
              className={`bg-blue-600 hover:bg-blue-800 text-white transition-colors duration-200 flex items-center gap-2`}
              onClick={handleSavePreference}
              disabled={isLoadingUpdate}
            >
              {isLoadingUpdate ? (
                <>
                  <svg
                    className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Saving...
                </>
              ) : "Save Preferences"}
            </Button>
          </CardFooter>
        </Card>
      </div>
    </div>
  );
}
